import { ColumnDef } from '@tanstack/react-table';
import { t } from 'i18next';
import { Link } from 'react-router';

import TimestampTooltip from '@/components/atoms/TimestampTooltip/TimestampTooltip';
import { Badge } from '@/components/ui/badge';
import { SortableHeader } from '@/components/ui/table/sortable-header';
import { cn } from '@/lib/utils';
import type { AccountDebt, PtpStatus } from '@/shared/types/contacts';
import { COLUMN_PRESETS } from '@/util/constants';
import { timeFromNow } from '@/util/dateUtils';

import { DebtActionsCell } from '../components/DebtActionsCell';
import {
  computeDpd,
  DEBT_TYPE_KEY,
  dpdTone,
  formatDueDate,
  formatMoney,
  TONE_CLASS,
} from '../components/DebtsTab';
import { DEBT_STATUS_KEY } from '../constants';

export const PTP_STATUS_KEY: Record<PtpStatus, string> = {
  none: 'contacts.debts.ptp_status_value.none',
  promised: 'contacts.debts.ptp_status_value.promised',
  kept: 'contacts.debts.ptp_status_value.kept',
  broken: 'contacts.debts.ptp_status_value.broken',
};

export const FLAG_KEY = {
  cease_desist: 'contacts.debts.flag.cease_desist',
  debt_disputed: 'contacts.debts.flag.debt_disputed',
  coerced_debt: 'contacts.debts.flag.coerced_debt',
  refusal_to_pay: 'contacts.debts.flag.refusal_to_pay',
  fraud_or_identity_theft: 'contacts.debts.flag.fraud_or_identity_theft',
} as const satisfies Partial<Record<keyof AccountDebt, string>>;

export const FLAGS = Object.keys(FLAG_KEY) as (keyof typeof FLAG_KEY)[];

/** Column id → `sort` field accepted by `GET /v1/debts`. */
export const DEBT_SORT_FIELD: Record<string, string> = {
  balance: 'current_balance',
  due_date: 'due_date',
  created: 'created',
  updated: 'updated',
};

const Empty = () => <span className="text-foreground-muted">—</span>;

const Money = ({ amount, currency }: { amount?: number; currency?: string }) => {
  const value = formatMoney(amount, currency);
  if (!value) {
    return <Empty />;
  }
  return (
    <span className="text-sm text-foreground tabular-nums">
      {value} <span className="text-foreground-muted">{currency}</span>
    </span>
  );
};

const Timestamp = ({ value }: { value?: string }) => {
  if (!value) {
    return <Empty />;
  }
  return (
    <TimestampTooltip timestamp={value}>
      <span className="text-sm text-foreground">{timeFromNow(value)}</span>
    </TimestampTooltip>
  );
};

export const debtColumns = (slug: string): ColumnDef<AccountDebt>[] => [
  {
    id: 'external_id',
    header: () => t('contacts.debts.external_id'),
    cell: ({ row }) => (
      <span className="block truncate font-medium">
        {row.original.debt_external_id || row.original.debt_id}
      </span>
    ),
    enableHiding: false,
    ...COLUMN_PRESETS.MD,
    size: 160,
    meta: { preset: 'MD', className: 'pl-6', label: 'contacts.debts.external_id' },
  },
  {
    id: 'primary_party',
    header: () => t('contacts.debts.primary_party'),
    cell: ({ row }) => {
      const { parties } = row.original;
      const party = parties.find((p) => p.role === 'primary_borrower') ?? parties[0];
      if (!party) {
        return (
          <Badge type="secondary" flavour="warning" rounded={false}>
            {t('contacts.debts.no_party')}
          </Badge>
        );
      }
      const others = parties.length - 1;
      return (
        <div className="flex min-w-0 items-center gap-1">
          <Link
            to={`/${slug}/contacts/${party.contact_id}`}
            onClick={(e) => e.stopPropagation()}
            className="truncate text-sm text-foreground hover:underline"
          >
            {party.display_name || t('contacts.unnamed')}
          </Link>
          {others > 0 && <span className="shrink-0 text-xs text-foreground-muted">+{others}</span>}
        </div>
      );
    },
    ...COLUMN_PRESETS.MD,
    meta: { preset: 'MD', label: 'contacts.debts.primary_party' },
  },
  {
    id: 'balance',
    header: ({ column }) => (
      <SortableHeader column={column}>{t('contacts.debts.balance')}</SortableHeader>
    ),
    cell: ({ row }) => (
      <Money amount={row.original.current_balance} currency={row.original.currency} />
    ),
    ...COLUMN_PRESETS.MD,
    size: 140,
    meta: { preset: 'MD', label: 'contacts.debts.balance' },
  },
  {
    id: 'original_balance',
    header: () => t('contacts.debts.original_balance'),
    cell: ({ row }) => (
      <Money amount={row.original.original_balance} currency={row.original.currency} />
    ),
    ...COLUMN_PRESETS.MD,
    size: 140,
    meta: { preset: 'MD', label: 'contacts.debts.original_balance', defaultHidden: true },
  },
  {
    id: 'status',
    header: () => t('common.status'),
    cell: ({ row }) => (
      <Badge type="secondary" flavour="neutral" rounded={false}>
        {t(DEBT_STATUS_KEY[row.original.status])}
      </Badge>
    ),
    ...COLUMN_PRESETS.MD,
    size: 112,
    meta: { preset: 'MD', label: 'common.status' },
  },
  {
    id: 'due_date',
    header: ({ column }) => (
      <SortableHeader column={column}>{t('contacts.debts.due_date')}</SortableHeader>
    ),
    cell: ({ row }) => {
      const { due_date, status } = row.original;
      const date = formatDueDate(due_date);
      if (!date) {
        return <Empty />;
      }
      const dpd = status === 'active' ? computeDpd(due_date) : null;
      return (
        <div className="flex flex-col">
          <span className="text-sm text-foreground">{date}</span>
          {!!dpd && (
            <span className={cn('text-xs', TONE_CLASS[dpdTone(dpd)])}>
              {t('contacts.debts.days_past_due', { 0: dpd })}
            </span>
          )}
        </div>
      );
    },
    ...COLUMN_PRESETS.MD,
    size: 140,
    meta: { preset: 'MD', label: 'contacts.debts.due_date' },
  },
  {
    id: 'ptp',
    header: () => t('contacts.debts.ptp_status'),
    cell: ({ row }) => {
      const { ptp_status, ptp_due_date } = row.original;
      if (!ptp_status || ptp_status === 'none') {
        return <Empty />;
      }
      return (
        <div className="flex flex-col">
          <span className="text-sm text-foreground">{t(PTP_STATUS_KEY[ptp_status])}</span>
          <span className="text-xs text-foreground-muted">{formatDueDate(ptp_due_date)}</span>
        </div>
      );
    },
    ...COLUMN_PRESETS.MD,
    size: 128,
    meta: { preset: 'MD', label: 'contacts.debts.ptp_status' },
  },
  {
    id: 'flags',
    header: () => t('contacts.debts.flags'),
    cell: ({ row }) => {
      const flags = FLAGS.filter((flag) => row.original[flag]);
      if (flags.length === 0) {
        return <Empty />;
      }
      return (
        <div className="flex flex-wrap gap-1">
          {flags.map((flag) => (
            <Badge key={flag} type="secondary" flavour="destructive" rounded={false}>
              {t(FLAG_KEY[flag])}
            </Badge>
          ))}
        </div>
      );
    },
    ...COLUMN_PRESETS.MD,
    size: 200,
    meta: { preset: 'MD', label: 'contacts.debts.flags' },
  },
  {
    id: 'type',
    header: () => t('contacts.debts.type_label'),
    cell: ({ row }) => {
      const { debt_type } = row.original;
      if (!debt_type) {
        return <Empty />;
      }
      return <span className="text-sm text-foreground">{t(DEBT_TYPE_KEY[debt_type])}</span>;
    },
    ...COLUMN_PRESETS.MD,
    size: 128,
    meta: { preset: 'MD', label: 'contacts.debts.type_label', defaultHidden: true },
  },
  {
    id: 'creditor',
    header: () => t('contacts.debts.creditor'),
    cell: ({ row }) => {
      const creditor = row.original.current_creditor || row.original.original_creditor;
      if (!creditor) {
        return <Empty />;
      }
      return <span className="block truncate text-sm text-foreground">{creditor}</span>;
    },
    ...COLUMN_PRESETS.MD,
    size: 160,
    meta: { preset: 'MD', label: 'contacts.debts.creditor', defaultHidden: true },
  },
  {
    id: 'created',
    header: ({ column }) => <SortableHeader column={column}>{t('common.created')}</SortableHeader>,
    cell: ({ row }) => <Timestamp value={row.original.created} />,
    ...COLUMN_PRESETS.MD,
    size: 128,
    meta: { preset: 'MD', label: 'common.created', defaultHidden: true },
  },
  {
    id: 'updated',
    header: ({ column }) => (
      <SortableHeader column={column}>{t('common.last_updated')}</SortableHeader>
    ),
    cell: ({ row }) => <Timestamp value={row.original.updated} />,
    ...COLUMN_PRESETS.MD,
    size: 128,
    meta: { preset: 'MD', label: 'common.last_updated' },
  },
  {
    id: 'actions',
    header: () => '',
    cell: ({ row }) => <DebtActionsCell debt={row.original} />,
    enableHiding: false,
    enableSorting: false,
    ...COLUMN_PRESETS.SM,
    meta: { preset: 'SM' },
  },
];
